import { openChat } from '@/lib/chatProvider'
import clsx from 'clsx'
import { useScreenMatch } from 'lib/utilities/screens'
import Button from '../Button/Button'
import { HelpCenterButton } from '../HelpCenter/HelpCenter'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'
import Logo, { Brand } from '../Logo/Logo'
import DropdownLinks from './DropdownLinks'
import MobileMenuModal, { useModal } from './MobileMenuModal'
import { PrimaryHeader } from './SiteHeader'

interface NavigationProps extends Omit<PrimaryHeader, 'type'> {
  brand: Brand
  logoLink?: string
  hideAccountIcon?: boolean
}

export default function Navigation({
  accountText,
  brand,
  brands,
  hideAccountIcon,
  links,
  logoLink = '/',
  button,
  helpLinks,
  userLinks,
}: NavigationProps) {
  const isDesktop = useScreenMatch('narrative')
  const openDialog = useModal((state) => state.openDialog)
  const logoBrand = (brand + (isDesktop ? 'dark' : 'textonlydark')) as Brand
  let renderButton: React.ReactNode | null = null
  if (button) {
    if (
      typeof button === 'object' &&
      ('href' in button || 'onClick' in button)
    ) {
      const btn = button as {
        name: string
        href?: string
        onClick?: () => void
      }
      renderButton = (
        <Button
          link={btn.href ? { href: btn.href } : undefined}
          onClick={btn.onClick}
          className="type-body-9 !min-h-12"
        >
          {btn.name}
        </Button>
      )
    } else {
      renderButton = button as React.ReactNode
    }
  }

  return (
    <>
      <div className="py-4 bg-white lg:border-t lg:border-neutral-400">
        <div className="container">
          <div className="col-span-full flex justify-between items-center">
            {!isDesktop && (
              <button
                type="button"
                onClick={openDialog}
                aria-label="Open navigation menu"
              >
                <Icon icon="menu" className="w-6 h-6 align-middle" />
              </button>
            )}
            <Link
              href={logoLink}
              className="flex items-center focus:outline-none focus-visible:outline-none"
            >
              <Logo
                brand={logoBrand}
                width={!isDesktop ? '170' : undefined}
                height={!isDesktop ? '41' : undefined}
              />
            </Link>
            <div className="flex gap-x-10">
              {isDesktop && links && (
                <ul className="flex items-center gap-x-8">
                  {links.map(({ name, href }, key) => (
                    <li key={key}>
                      <Link
                        href={href}
                        inheritFont
                        className={clsx(
                          'type-body-6 !px-0',
                          '!no-underline hover:!underline underline-offset-[0.3em] hover:decoration-[2px] decoration-theme-secondary',
                        )}
                      >
                        {name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center gap-x-6">
                {isDesktop ? (
                  <>
                    <HelpCenterButton
                      render={(buttonProps) => (
                        <button
                          className="flex items-center focus:outline-none focus-visible:outline-none"
                          {...buttonProps}
                        >
                          <Icon
                            icon="help-circle-outline"
                            className="w-6 h-6 align-middle"
                          />
                        </button>
                      )}
                    />
                    {!hideAccountIcon && (
                      <DropdownLinks
                        icon="account-circle"
                        options={userLinks}
                        accountText={accountText}
                        ariaLabel="Account options menu"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={openChat}
                      aria-label="Chat Support"
                      className="relative z-10 flex items-center justify-center min-w-[44px] min-h-[44px]"
                    >
                      <Icon
                        icon="chat-bubble"
                        className="w-6 h-6 align-middle"
                      />
                    </button>
                    {helpLinks.length > 1 ? (
                      <DropdownLinks
                        icon="help-circle-outline"
                        options={helpLinks}
                        ariaLabel="Help links menu"
                      />
                    ) : helpLinks.length === 1 ? (
                      <Link
                        href={helpLinks[0].href}
                        className="flex items-center focus:outline-none focus-visible:outline-none"
                      >
                        <Icon
                          icon="help-circle-outline"
                          className="w-6 h-6 align-middle"
                        />
                      </Link>
                    ) : (
                      <HelpCenterButton
                        render={(buttonProps) => (
                          <button
                            className="flex items-center focus:outline-none focus-visible:outline-none"
                            {...buttonProps}
                          >
                            <Icon
                              icon="help-circle-outline"
                              className="w-6 h-6 align-middle"
                            />
                          </button>
                        )}
                      />
                    )}
                    <DropdownLinks
                      icon="account-circle"
                      options={userLinks}
                      ariaLabel="Account options menu"
                    />
                  </>
                )}
                {isDesktop && renderButton}
              </div>
            </div>
          </div>
        </div>
      </div>
      <MobileMenuModal
        brand={brand}
        brands={brands}
        links={links}
        button={button}
        helpLinks={helpLinks}
        userLinks={userLinks}
      />
    </>
  )
}
